// The newest saved version of each edit project, as the team's stream tells it (spec
// docs/specs/editor/live-sync-stream.md). An editor open on a project whose head passes the
// version it holds reads what changed. And the team's stream resyncs: a `resync` may have skipped
// saves, with no frame to come for them, so an open editor reads what changed after each. Kept
// in the query cache, never fetched: only `event-handlers.ts` writes them.
import { skipToken, useQuery, type QueryClient } from "@tanstack/react-query";

/** The cache's key of a project's head. */
export function projectHeadKey(teamId: string, projectId: string): readonly [string, string, string] {
  return ["edit-project-head", teamId, projectId];
}

/** Notes `version` as the project's head. Never lowered: a resumed stream can send a frame again. */
export function noteProjectHead(queryClient: QueryClient, teamId: string, projectId: string, version: number): void {
  queryClient.setQueryData<number>(projectHeadKey(teamId, projectId), (known) => Math.max(known ?? 0, version));
}

/** The project's newest saved version the stream told of, or undefined before any. */
export function useProjectHead(teamId: string, projectId: string): number | undefined {
  return useQuery<number>({ queryKey: projectHeadKey(teamId, projectId), queryFn: skipToken }).data;
}

/** The cache's key of the team's stream resyncs. */
export function projectResyncsKey(teamId: string): readonly [string, string] {
  return ["edit-project-resyncs", teamId];
}

/** Notes a resync of the team's stream: the saves it skipped send no frame, so every open editor reads again. */
export function noteProjectResync(queryClient: QueryClient, teamId: string): void {
  queryClient.setQueryData<number>(projectResyncsKey(teamId), (resyncs) => (resyncs ?? 0) + 1);
}

/** How many resyncs of the team's stream were noted: an editor reads what changed when it moves. */
export function useProjectResyncs(teamId: string): number {
  return useQuery<number>({ queryKey: projectResyncsKey(teamId), queryFn: skipToken }).data ?? 0;
}
